"use client";

import type React from "react";
import { useState } from "react";
import {
  Container,
  Card,
  CardBody,
  VStack,
  Heading,
  Text,
  Alert,
  AlertIcon,
  FormControl,
  FormLabel,
  Input,
  Button,
  Image,
  Link as ChakraLink,
  Box,
} from "@chakra-ui/react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";

export default function PasswordReset() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handlePasswordReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/set-new-password`,
      });

      if (error) throw error;

      setSuccess(true);
      navigate("/password-reset-otp", { state: { email } });
    } catch (error: any) {
      setError(error.error_description || error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxW="md" py={12}>
      <Card>
        <CardBody p={8}>
          <VStack spacing={6}>
            <VStack spacing={2} textAlign="center">
              <Image
                src="/main-logo.png"
                alt="RadiusDocs AI Logo"
                maxH="200px"
                objectFit="contain"
              />
              <Heading size="lg" color="gray.700">
                Reset Password
              </Heading>
              <Text color="gray.600">
                Enter your email address and we'll send you a verification code
                to reset your password
              </Text>
            </VStack>

            {error && (
              <Alert status="error" borderRadius="md" textColor={"red.600"}>
                <AlertIcon />
                {error}
              </Alert>
            )}

            {success ? (
              <VStack spacing={4}>
                <Alert
                  status="success"
                  borderRadius="md"
                  textColor={"green.600"}
                >
                  <AlertIcon />
                  Password reset email sent. Please check your inbox.
                </Alert>
                <Button as={Link} to="/login" colorScheme="primary" w="100%">
                  Back to Sign In
                </Button>
              </VStack>
            ) : (
              <Box as="form" onSubmit={handlePasswordReset} w="100%">
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel color="gray.600">Email</FormLabel>
                    <Input
                      type="email"
                      placeholder="Your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      bg="gray.50"
                      _placeholder={{ color: "gray.500" }}
                    />
                  </FormControl>

                  <Button
                    type="submit"
                    colorScheme="primary"
                    size="lg"
                    w="100%"
                    isLoading={loading}
                    loadingText="Sending..."
                  >
                    Send Reset Instructions
                  </Button>

                  <ChakraLink as={Link} to="/login" color="primary.600">
                    Back to Sign In
                  </ChakraLink>
                </VStack>
              </Box>
            )}
          </VStack>
        </CardBody>
      </Card>
    </Container>
  );
}
